'use client';

/**
 * ConsentLayer — the analytics consent bar. Analytics stay OFF until the
 * visitor chooses; the behavioral layer itself is local and needs no consent
 * because it never leaves the browser. Appears 6s after first load when no
 * choice is stored, and reappears immediately if the choice is reset from
 * the privacy page.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  CONSENT_RESET_EVENT,
  getConsent,
  loadAnalytics,
  setConsent,
  type ConsentState,
} from '../analytics/gate';
import { useLang } from '../i18n/client';
import { getDict } from '../i18n/dictionary';
import { localePath, type Lang } from '../i18n/config';
import styles from './ConsentLayer.module.css';

const SHOW_DELAY_MS = 6_000;

/**
 * Landmark label for the consent region — component-local, not in the shared
 * chrome dictionary (see openIssues: a dict.consent.regionAria would fit here).
 */
const REGION_ARIA: Record<Lang, string> = {
  en: 'Privacy and analytics consent',
  ru: 'Согласие на конфиденциальность и аналитику',
};

export default function ConsentLayer() {
  const lang = useLang();
  const t = getDict(lang).consent;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      // Apply the stored choice on every load; the gate no-ops without consent.
      loadAnalytics(existing);
      return;
    }
    const t = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Reopen when the privacy page's ConsentReset island clears the choice.
  useEffect(() => {
    const onReset = () => setVisible(true);
    window.addEventListener(CONSENT_RESET_EVENT, onReset);
    return () => window.removeEventListener(CONSENT_RESET_EVENT, onReset);
  }, []);

  const choose = (analytics: boolean) => {
    const consent: ConsentState = { analytics, marketing: false, ts: Date.now() };
    setConsent(consent);
    loadAnalytics(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.bar} role="region" aria-label={REGION_ARIA[lang]}>
      <div className={`panel panel--solid ${styles.inner}`}>
        <p className={styles.text}>
          {t.text}{' '}
          <Link href={localePath(lang, '/privacy')}>{t.privacyLink}</Link>
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => choose(false)}
          >
            {t.essential}
          </button>
          <button type="button" className="btn" onClick={() => choose(true)}>
            {t.allow}
          </button>
        </div>
      </div>
    </div>
  );
}
